import type { PoCorrection } from '@repo/sdk/gen'
import { zPoCorrectionFilters, zPoListCorrectionsData } from '@repo/sdk/zod'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import type { TKey } from '@/lib/i18n'

export const CORRECTION_STATUS_KEYS = {
	pending: 'po:corrections.status.pending',
	ok: 'po:corrections.status.ok',
	ko: 'po:corrections.status.ko'
} as const satisfies Record<PoCorrection['status'], TKey>

export const CORRECTION_SORT_FIELDS =
	zPoListCorrectionsData.shape.body.shape.sort.unwrap().shape.field.enum
export const correctionSearchSchema = z.object({
	correction: z.string().optional().catch(undefined),
	correctionSearch: zPoCorrectionFilters.shape.search
		.unwrap()
		.default('')
		.catch(''),
	correctionStatus: zPoCorrectionFilters.shape.status
		.unwrap()
		.element.nullable()
		.optional()
		.catch(undefined),
	correctionRun: z.string().optional().catch(undefined),
	correctionAuthor: z.string().optional().catch(undefined),
	correctionFrom: z.iso.date().optional().catch(undefined),
	correctionTo: z.iso.date().optional().catch(undefined),
	correctionPage: z.number().int().min(1).default(1).catch(1),
	correctionBy: z.enum(CORRECTION_SORT_FIELDS).optional().catch(undefined),
	correctionDirection: z.enum(['asc', 'desc']).optional().catch(undefined)
})

// Null explicitly means all states, so clearing Pending survives a reload.
// The document tab keeps its unfiltered default.
export const globalCorrectionSearchSchema = correctionSearchSchema.extend({
	correctionStatus: correctionSearchSchema.shape.correctionStatus
		.default('pending')
		.catch('pending')
})

// Retired correction filters must not survive in copied links or affect the
// other tabs. The middleware removes only these three obsolete parameters.
export function stripRemovedCorrectionFilters<
	TSearch extends Record<string, unknown>
>(props: { search: TSearch; next: (search: TSearch) => TSearch }) {
	return {
		...props.next(props.search),
		correctionPublisher: undefined,
		correctionDistributor: undefined,
		correctionSchoolType: undefined
	}
}

// Both entry points own their search in the URL; the prefix keeps this tab
// independent from the document's proposals and export filters.
export function useCorrectionsView() {
	const search = useSearch({ strict: false })
	const navigate = useNavigate()
	const params = correctionSearchSchema.parse(search)
	return {
		params,
		setView: (patch: Partial<z.infer<typeof correctionSearchSchema>>) => {
			navigate({
				to: '.',
				search: (prev: Record<string, unknown>) => ({
					...prev,
					...patch
				})
			})
		}
	}
}

export function correctionFilters(
	params: z.infer<typeof correctionSearchSchema>,
	runId?: string
) {
	return {
		runId: runId ?? params.correctionRun,
		status: params.correctionStatus ? [params.correctionStatus] : undefined,
		authorIds: params.correctionAuthor
			? [params.correctionAuthor]
			: undefined,
		search: params.correctionSearch || undefined,
		correctedAt:
			params.correctionFrom || params.correctionTo
				? {
						from: dayBoundary(params.correctionFrom, false),
						to: dayBoundary(params.correctionTo, true)
					}
				: undefined
	}
}

function dayBoundary(value: string | undefined, end: boolean) {
	if (!value) {
		return undefined
	}
	const [year, month, day] = value.split('-').map(Number)
	return new Date(
		year,
		month - 1,
		day,
		end ? 23 : 0,
		end ? 59 : 0,
		end ? 59 : 0,
		end ? 999 : 0
	).toISOString()
}
